import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useFavorites } from "../hooks/useFavorites";
import { useStudies } from "../hooks/useStudies";

export default function Favorites() {
  const { studies } = useStudies();
  const [expandedTitles, setExpandedTitles] = useState({});
  const { isFavorite, toggleFavorite } = useFavorites();

  const sanitize = (val) => (val && val !== "nan" ? val : "N/A");

  const grouped = studies.reduce((acc, study) => {
    const title = sanitize(study.Article_title);
    if (!acc[title]) acc[title] = [];
    acc[title].push(study);
    return acc;
  }, {});

  const favoritedGroups = Object.entries(grouped).filter(([title]) => isFavorite(title));

  const toggleExpand = (title) =>
    setExpandedTitles((prev) => ({ ...prev, [title]: !prev[title] }));

  return (
    <div
      style={{
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        padding: "2rem",
        maxWidth: "1100px",
        margin: "0 auto",
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
      }}
    >
      <h2
        style={{
          color: "#0079C0",
          fontSize: "2rem",
          fontWeight: "700",
          marginBottom: "0.5rem",
          display: "flex",
          alignItems: "center",
          gap: "0.6rem",
        }}
      >
        ♥ My Favourites
      </h2>
      <p style={{ color: "#6b7280", marginBottom: "2rem", fontSize: "0.95rem" }}>
        {favoritedGroups.length} saved {favoritedGroups.length === 1 ? "study" : "studies"}
      </p>

      {favoritedGroups.length === 0 ? (
        <div
          style={{
            background: "#fff",
            borderRadius: "16px",
            padding: "4rem 2rem",
            textAlign: "center",
            boxShadow: "0 6px 24px rgba(0,0,0,0.08)",
            border: "1px solid #e5e7eb",
          }}
        >
          <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>♡</div>
          <h3 style={{ color: "#374151", marginBottom: "0.5rem" }}>No favourites yet</h3>
          <p style={{ color: "#6b7280", marginBottom: "1.5rem" }}>
            Click the ♡ button on any study to save it here.
          </p>
          <Link
            to="/search"
            style={{
              display: "inline-block",
              background: "linear-gradient(135deg, #0079C0 0%, #005f9e 100%)",
              color: "white",
              padding: "0.75rem 1.5rem",
              borderRadius: "8px",
              textDecoration: "none",
              fontWeight: "600",
            }}
          >
            Browse Studies
          </Link>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          {favoritedGroups.map(([title, rows]) => (
            <div
              key={title}
              style={{
                background: "#fff",
                borderRadius: "12px",
                padding: "1.5rem",
                boxShadow: "0 4px 15px rgba(0,0,0,0.08)",
                border: "1px solid #e5e7eb",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "1rem" }}>
                <div style={{ flex: 1 }}>
                  <h3
                    style={{
                      fontSize: "1.05rem",
                      fontWeight: "600",
                      color: "#1f2937",
                      marginBottom: "0.4rem",
                      lineHeight: "1.4",
                    }}
                  >
                    {title}
                  </h3>
                  <p style={{ fontSize: "0.85rem", color: "#6b7280" }}>
                    {rows.length} comparison type{rows.length !== 1 ? "s" : ""}
                  </p>
                </div>
                <div style={{ display: "flex", gap: "0.5rem", flexShrink: 0 }}>
                  <button
                    onClick={() => toggleFavorite(title)}
                    title="Remove from favourites"
                    style={{
                      background: "rgba(239,68,68,0.08)",
                      border: "1px solid rgba(239,68,68,0.3)",
                      borderRadius: "6px",
                      padding: "0.45rem 0.75rem",
                      cursor: "pointer",
                      color: "#ef4444",
                      fontSize: "0.85rem",
                      fontWeight: "600",
                      transition: "all 0.2s",
                    }}
                  >
                    ♥ Remove
                  </button>
                  <button
                    onClick={() => toggleExpand(title)}
                    style={{
                      background: "rgba(0,121,192,0.1)",
                      border: "none",
                      borderRadius: "6px",
                      padding: "0.45rem 0.75rem",
                      cursor: "pointer",
                      color: "#0079C0",
                      fontSize: "0.85rem",
                      fontWeight: "500",
                    }}
                  >
                    {expandedTitles[title] ? "Hide" : "Show"} Details
                  </button>
                </div>
              </div>

              {expandedTitles[title] && (
                <div>
                  {/* Shared metadata — shown once */}
                  <div style={{
                    borderTop: "1px solid #e5e7eb",
                    paddingTop: "0.75rem",
                    marginTop: "0.75rem",
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                    gap: "0.5rem",
                    fontSize: "0.85rem",
                    color: "#374151",
                  }}>
                    <div><strong>Authors:</strong> {sanitize(rows[0].Authors)}</div>
                    <div><strong>Journal:</strong> {sanitize(rows[0].Journal)}</div>
                    <div><strong>Year:</strong> {(rows[0].Year_of_publication || "—").toString().replace(/,/g, "")}</div>
                    <div><strong>Category:</strong> {sanitize(rows[0].Age_category)}</div>
                    <div><strong>Epilepsy Type:</strong> {sanitize(rows[0].Epilepsy_type)}</div>
                    <div><strong>Sample Size:</strong> {sanitize(rows[0].Sample_size_Patients)} patients</div>
                  </div>

                  {/* One row per comparison type */}
                  <div style={{ marginTop: "0.75rem", display: "flex", flexDirection: "column", gap: "0.4rem" }}>
                    {rows.map((study, i) => (
                      <Link
                        key={i}
                        to={`/study/${study.id}`}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "0.5rem 0.75rem",
                          borderRadius: "6px",
                          background: "rgba(0,121,192,0.06)",
                          border: "1px solid rgba(0,121,192,0.12)",
                          textDecoration: "none",
                          color: "#1f2937",
                          fontSize: "0.875rem",
                          transition: "background 0.2s",
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = "rgba(0,121,192,0.12)"}
                        onMouseLeave={(e) => e.currentTarget.style.background = "rgba(0,121,192,0.06)"}
                      >
                        <span><strong>Comparison:</strong> {sanitize(study.Comparison)}</span>
                        <span style={{ color: "#0079C0", fontWeight: "500", whiteSpace: "nowrap", marginLeft: "1rem" }}>View →</span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
